import React, { FC, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { doLogin } from '../../slice';
import { Grid, IconButton, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { LoadingButton } from 'features/common/components/LoadingButton';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		paper: {
			padding: theme.spacing(4),
			width: '100%',
			maxWidth: '400px',
			background: 'linear-gradient(145deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.95) 100%)',
			backdropFilter: 'blur(20px)',
			borderRadius: '24px',
			margin: '0 auto',
			boxShadow: '0 25px 50px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.3)',
			border: '1px solid rgba(255,255,255,0.4)',
			transition: 'all 0.3s ease',
			boxSizing: 'border-box',
			'&:hover': {
				transform: 'translateY(-2px)',
				boxShadow: '0 30px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.3)',
			},
			[theme.breakpoints.down('sm')]: {
				padding: theme.spacing(2.5),
				borderRadius: '20px',
				maxWidth: '100%',
				minWidth: 'unset',
			},
		},
		title: {
			background: 'linear-gradient(135deg, #1976d2 0%, #673ab7 100%)',
			backgroundClip: 'text',
			WebkitBackgroundClip: 'text',
			WebkitTextFillColor: 'transparent',
			fontWeight: 700,
			textAlign: 'center',
			marginBottom: theme.spacing(3),
			fontSize: '1.75rem',
			[theme.breakpoints.down('sm')]: {
				fontSize: '1.5rem',
			},
		},
		formGrid: {
			'& .MuiGrid-item': {
				paddingTop: theme.spacing(1.5),
			},
		},
		textField: {
			width: '100%',
			minWidth: 0,
			'& .MuiFilledInput-root': {
				borderRadius: '12px',
				backgroundColor: 'rgba(255,255,255,0.8)',
				border: '1px solid rgba(0,0,0,0.1)',
				transition: 'all 0.3s ease',
				width: '100%',
				minWidth: 0,
				'&:hover': {
					backgroundColor: 'rgba(255,255,255,0.95)',
					borderColor: theme.palette.primary.main,
					transform: 'translateY(-1px)',
					boxShadow: '0 4px 12px rgba(25,118,210,0.15)',
				},
				'&.Mui-focused': {
					backgroundColor: '#fff',
					borderColor: theme.palette.primary.main,
					boxShadow: '0 0 0 3px rgba(25,118,210,0.1)',
				},
				'&:before, &:after': {
					display: 'none',
				},
			},
			'& .MuiInputLabel-root': {
				fontWeight: 500,
				color: theme.palette.text.secondary,
				transform: 'translate(14px, 20px) scale(1)',
				transition: 'all 0.2s ease-in-out',
				whiteSpace: 'nowrap',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				'&.Mui-focused, &.MuiInputLabel-shrink': {
					color: theme.palette.primary.main,
					transform: 'translate(14px, 8px) scale(0.75)',
				},
			},
			'& .MuiFilledInput-input': {
				paddingTop: '24px',
				paddingBottom: '8px',
				width: '100%',
				minWidth: 0,
			},
		},
		passwordField: {
			paddingLeft: '12px',
		},
		submitButton: {
			borderRadius: '12px',
			padding: theme.spacing(1.5),
			fontWeight: 600,
			fontSize: '1rem',
			textTransform: 'none',
			background: 'linear-gradient(135deg, #1976d2 0%, #673ab7 100%)',
			boxShadow: '0 8px 20px rgba(25,118,210,0.3)',
			transition: 'all 0.3s ease',
			'&:hover': {
				transform: 'translateY(-2px)',
				boxShadow: '0 12px 28px rgba(25,118,210,0.4)',
				background: 'linear-gradient(135deg, #1565c0 0%, #512da8 100%)',
			},
			'&:disabled': {
				background: theme.palette.grey[300],
				boxShadow: 'none',
				transform: 'none',
			},
		},
		errorContainer: {
			marginTop: theme.spacing(1),
		},
		errorText: {
			color: theme.palette.error.main,
			fontSize: '0.875rem',
			fontWeight: 500,
			textAlign: 'center',
			padding: theme.spacing(1.5),
			backgroundColor: 'rgba(244, 67, 54, 0.1)',
			borderRadius: '8px',
			border: '1px solid rgba(244, 67, 54, 0.2)',
		},
	}),
);

export const LoginForm: FC = () => {
	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [username, setUsername] = useState<string>('');
	const [password, setPassword] = useState<string>('');
	const classes = useStyles();
	const { status, errorMessage } = useSelector((state: RootState) => state.auth);
	const dispatch = useDispatch();

	const handlePasswordToggle = () => setShowPassword((prev) => !prev);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (username && password) {
			dispatch(doLogin({ password, login: username }));
		}
	};

	return (
		<Paper elevation={0} className={classes.paper}>
			<form onSubmit={handleSubmit}>
				<Typography variant="h4" className={classes.title}>
					Iniciar Sesión
				</Typography>

				<Grid container direction="column" spacing={2} className={classes.formGrid}>
					<Grid item xs={12}>
						<TextField
							fullWidth
							variant="filled"
							label="Usuario"
							className={classes.textField}
							value={username}
							onChange={(e) => setUsername(e.target.value)}
							required
						/>
					</Grid>

					<Grid item xs={12}>
						<TextField
							fullWidth
							variant="filled"
							label="Contraseña"
							className={classes.textField}
							type={showPassword ? 'text' : 'password'}
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							required
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<IconButton onClick={handlePasswordToggle} edge="end" size="medium">
											{showPassword ? <Visibility /> : <VisibilityOff />}
										</IconButton>
									</InputAdornment>
								),
							}}
						/>
					</Grid>

					{status === 'failed' && (
						<Grid item xs={12} className={classes.errorContainer}>
							<div className={classes.errorText}>{errorMessage}</div>
						</Grid>
					)}

					<Grid item xs={12} style={{ paddingTop: '24px' }}>
						<LoadingButton
							variant="contained"
							fullWidth
							type="submit"
							loading={status === 'loading'}
							className={classes.submitButton}
							size="large"
						>
							Ingresar
						</LoadingButton>
					</Grid>
				</Grid>
			</form>
		</Paper>
	);
};
